import {gql} from '@apollo/client';
import {naturalSortByKey} from '@benchling/js-utils';

import {getRegistrySupportedOligoAwareTypesGraphQL} from 'constants/RegistryConstants';
import {OligoAwareFolderItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {readQuerySafe, writeQuery} from 'graphql-client/ApolloWrappers';
import {BenchlingQueryResult, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';

import {
  GetTagSchemaSummariesListing,
  // eslint-disable-next-line camelcase
  GetTagSchemaSummariesListing_tagSchemasListing_TagSchemaListingNode_tagSchemas_TagSchemaNode as TagSchemaSummaryGQL,
  GetTagSchemaSummariesListingVariables,
} from './__generated/GetTagSchemaSummariesListing';

export const TAG_SCHEMA_SUMMARIES_QUERY = gql`
  query GetTagSchemaSummariesListing(
    $searchInput: TagSchemaListingSearchInput
    $limitOffsetInput: LimitOffsetInput
  ) @skipAudit @engComponent(name: SCHEMAS) {
    tagSchemasListing(searchInput: $searchInput, limitOffsetInput: $limitOffsetInput) {
      tagSchemas {
        ...TagSchemaSummaryGraphQL
      }
      total
    }
  }

  ${getFragmentWrapper('TagSchemaSummary').fragment}
`;

export interface UseTagSchemaSummariesListingQueryArgs {
  registryId?: string | null;
  oligoAwareFolderItemTypes?: Array<OligoAwareFolderItemType> | null;
}

export function addTagSchemaToTagSchemaSummariesQueryCache(
  tagSchema: TagSchemaSummaryGQL,
  registryId: string
): void {
  const variables: GetTagSchemaSummariesListingVariables = {
    searchInput: {
      registryId,
      oligoAwareFolderItemTypes: getRegistrySupportedOligoAwareTypesGraphQL(),
    },
  };
  const cached = readQuerySafe<GetTagSchemaSummariesListing, GetTagSchemaSummariesListingVariables>(
    {query: TAG_SCHEMA_SUMMARIES_QUERY},
    {variables}
  );
  if (!cached) {
    return;
  }
  const alreadyCached = cached.tagSchemasListing.tagSchemas.some((s) => s.id === tagSchema.id);
  if (alreadyCached) {
    return;
  }
  const updatedTagSchemas = naturalSortByKey([...cached.tagSchemasListing.tagSchemas, tagSchema], 'name');
  const newData: GetTagSchemaSummariesListing = {
    ...cached,
    tagSchemasListing: {
      ...cached.tagSchemasListing,
      tagSchemas: updatedTagSchemas,
      total: updatedTagSchemas.length,
    },
  };
  writeQuery<GetTagSchemaSummariesListing, GetTagSchemaSummariesListingVariables>(
    {query: TAG_SCHEMA_SUMMARIES_QUERY},
    newData,
    variables
  );
}

export default function useTagSchemaSummariesListingQuery({
  registryId,
  oligoAwareFolderItemTypes,
}: UseTagSchemaSummariesListingQueryArgs = {}): BenchlingQueryResult<GetTagSchemaSummariesListing> {
  return useBenchlingQuery<GetTagSchemaSummariesListing, GetTagSchemaSummariesListingVariables>(
    TAG_SCHEMA_SUMMARIES_QUERY,
    {
      variables: {
        searchInput: {
          registryId: registryId ?? null,
          oligoAwareFolderItemTypes:
            oligoAwareFolderItemTypes ?? getRegistrySupportedOligoAwareTypesGraphQL(),
        },
      },
    }
  );
}
